import {
  BufferGeometry,
  Camera,
  Group,
  Intersection,
  LineDashedMaterial,
  Material,
  MeshBasicMaterial,
  Object3D,
  OrthographicCamera,
  Raycaster,
  Scene,
  Texture,
  Vector3,
  WebGLRenderer,
} from "three";
import { Font } from "three/examples/jsm/loaders/FontLoader";
import font from "../../../fonts/RoundedMplus1c_Regular.json";
import * as Config from "../config/ViewerConfig";
import { AutoAttenuableLine } from "../object/AutoAttenuableLine";
import { AutoAttenuablePin } from "../object/AutoAttenuablePin";
import { AutoAttenuatableArrow } from "../object/AutoAttenuatableArrow";
import { AutoAttenuatableTextSprite } from "../object/AutoAttenuatableTextSprite";
import { ExtendedLineDashed } from "../object/ExtenedLineDashed";
import { Viewer } from "../viewer";
import {
  AutoAttenuableState,
  IMeasurementHandler,
  IMeasurementVisibleParams,
  MeasurementMode,
  MeasurementState,
  MeasurementType,
} from "./types";
import { getRoundRect } from "./utils";

export class VerticalMeasurement implements IMeasurementHandler {
  type: MeasurementType = MeasurementType.VERTICAL;
  _id: number;
  _points: Vector3[];
  value: number;

  private _group: Group;

  private _viewer: Viewer;
  private _mode: MeasurementMode;

  private _pin1: AutoAttenuablePin;
  private _pin2: AutoAttenuablePin;
  // private _lineGeometry: BufferGeometry;
  private _lineMaterial: MeshBasicMaterial;
  private _line: AutoAttenuableLine;

  // private _dottedLineGeometry: BufferGeometry;
  private _dottedLineMaterial: LineDashedMaterial;
  private _dottedLine: ExtendedLineDashed;

  // private _sphereGeometry: SphereBufferGeometry;
  private _sphereMaterial: MeshBasicMaterial;
  // private _sphere1: AutoAttenuatableSphere;
  // private _sphere2: AutoAttenuatableSphere;
  private _arrow1: AutoAttenuatableArrow;
  private _arrow2: AutoAttenuatableArrow;

  private _font: Font;
  // private _textMaterial: SpriteMaterial;
  private _textSprite: AutoAttenuatableTextSprite;

  private _textTextureContext: CanvasRenderingContext2D;
  private _textTexture: Texture;
  appearanceState: AutoAttenuableState;

  public get id(): number {
    return this._id;
  }

  constructor(viewer: Viewer, textScale: Config.ITextScale) {
    this._viewer = viewer;
    this.value = 0;
    this._points = [];
    this._group = new Group();
    this._viewer.getScene(Config.SCENE_TYPE.MEASUREMENT).add(this._group);
    this._mode = MeasurementMode.CREATE;
    this.appearanceState = "default";

    // this._lineGeometry = new BufferGeometry();
    this._lineMaterial = new MeshBasicMaterial({
      color: Config.NORMAL_MEASUREMENT_COLOR,
      depthTest: false,
      transparent: true,
    });
    this._line = new AutoAttenuableLine(0, this._lineMaterial);
    this._line.visible = false;
    this._group.add(this._line);

    // this._dottedLineGeometry = new BufferGeometry();
    this._dottedLineMaterial = new LineDashedMaterial({
      color: Config.NORMAL_MEASUREMENT_COLOR,
      dashSize: 0.2,
      gapSize: 0.1,
      depthTest: false,
      transparent: true,
    });
    this._dottedLine = new ExtendedLineDashed(
      // this._dottedLineGeometry,
      this._dottedLineMaterial
    );

    this._dottedLine.onBeforeRender = function (
      _renderer: WebGLRenderer,
      _scene: Scene,
      camera: Camera,
      _geom: BufferGeometry,
      material: Material
    ) {
      let scale = 1;
      // if (camera instanceof OrizuruPerspectiveCamera) {
      //     const frustumWidth = camera.getFrustumWidthAtDistance(
      //         cameraPlane.distanceToPoint(this.position)
      //     );
      //     scale = 0.007 * frustumWidth;
      // } else
      if (camera instanceof OrthographicCamera) {
        scale = ((camera.right - camera.left) / camera.zoom) * 0.03;
      } else {
        scale = this.position.distanceTo(viewer.getCamera().position) * 0.03;
      }

      (material as LineDashedMaterial).gapSize = scale / 2;
      (material as LineDashedMaterial).dashSize = scale;
    };
    this._dottedLine.visible = false;
    this._group.add(this._dottedLine);

    this._pin1 = new AutoAttenuablePin("0", this._viewer);
    this._pin1.visible = false;
    this._group.add(this._pin1);

    this._pin2 = new AutoAttenuablePin("1", this._viewer);
    this._pin2.visible = false;
    this._group.add(this._pin2);

    // this._sphereGeometry = new SphereBufferGeometry(1, 32, 16);
    this._sphereMaterial = new MeshBasicMaterial({
      color: Config.NORMAL_MEASUREMENT_COLOR,
      depthTest: false,
      transparent: true,
    });
    this._arrow1 = new AutoAttenuatableArrow(
      this._sphereMaterial,
      Config.MEASUREMENT_SPHERE_SIZE
    );
    this._arrow1.visible = false;
    // this._sphere1.scale.set(0.1, 0.1, 0.1);
    this._group.add(this._arrow1);

    this._arrow2 = new AutoAttenuatableArrow(
      this._sphereMaterial,
      Config.MEASUREMENT_SPHERE_SIZE
    );
    this._arrow2.visible = false;
    // this._sphere2.scale.set(0.1, 0.1, 0.1);
    this._group.add(this._arrow2);

    this._font = new Font(font);

    const textCanvas = document.createElement("canvas");
    textCanvas.width = 1000;
    textCanvas.height = 500;
    this._textTextureContext = textCanvas.getContext("2d");
    this._textTexture = new Texture(textCanvas);
    this._textTexture.needsUpdate = true;
    // this._textMaterial = new SpriteMaterial({ map: this._textTexture });
    this._textSprite = new AutoAttenuatableTextSprite(
      this._textTexture,
      textScale
    );
    this._textSprite.visible = false;
    this._group.add(this._textSprite);
  }

  private updateTextContext2d(state: MeasurementState) {
    const canvasWidth = this._textTextureContext.canvas.width;
    const canvasHeight = this._textTextureContext.canvas.height;
    this._textSprite.geometry.computeBoundingBox();
    this._textTextureContext.clearRect(0, 0, canvasWidth, canvasHeight);

    const borderThickness = 4;
    const fontsize = 44;
    const fontface = "Arial";
    const text = `${this.value.toFixed(3)} m`;
    const tab1Width = 16;
    const edgeConst = 1.05;

    this._textTextureContext.font = fontsize + "px " + fontface;

    const textHeight = fontsize;

    const textWidth = this._textTextureContext.measureText(text).width;

    this._textTextureContext.lineWidth = borderThickness;
    this._textTextureContext.fillStyle = {
      default: Config.DEFAULT_MEASUREMENT_TEXT_BGCOLOR,
      hovered: Config.HOVERED_MEASUREMENT_TEXT_BGCOLOR,
      selected: Config.SELECTED_MEASUREMENT_TEXT_BGCOLOR,
    }[state];
    this._textTextureContext.strokeStyle = "rgba(0,0,0,1.0)"; //
    const roundedRectWidth = tab1Width + textWidth + borderThickness * 2;
    const roundedRectHeight = textHeight + borderThickness * 2;
    getRoundRect(
      this._textTextureContext,
      borderThickness / 2,
      borderThickness / 2,
      roundedRectWidth,
      roundedRectHeight,
      6
    );

    this._textTextureContext.fillStyle = "rgba(255,255,255,1.0)"; //text color
    this._textTextureContext.strokeStyle = "rgba(255,255,255,1.0)"; //text color

    this._textTextureContext.fillText(text, tab1Width, textHeight);

    // テキストテクスチャが間違いなく収まる幅へ定数で調整
    const newCanvasWidth = Math.ceil(roundedRectWidth * edgeConst);
    const newCanvasHeight = Math.ceil(roundedRectHeight * edgeConst);

    const geom = this._textSprite.geometry;
    const uvs = geom.attributes["uv"];
    uvs.setXY(0, 0, 1 - newCanvasHeight / canvasHeight);
    uvs.setXY(
      1,
      newCanvasWidth / canvasWidth,
      1 - newCanvasHeight / canvasHeight
    );
    uvs.setXY(2, newCanvasWidth / canvasWidth, 1);
    uvs.setXY(3, 0, 1);
    uvs.needsUpdate = true;
    this._textSprite.aspect = newCanvasHeight / newCanvasWidth;

    this._textTexture.needsUpdate = true;
  }

  private calculateDistance() {
    this.value = Math.abs(this._points[0].z - this._points[1].z);
  }

  public setPoints(points: Vector3[]) {
    this._points = points.map((p) => p.clone());
    this._id =
      this._id === undefined ? this._viewer.generateMeasurementId() : this._id;
    // this._mode = MeasurementMode.INACTIVE;
    this.calculateDistance();
    this.render();
  }

  public dispose() {
    this._points.length = 0;
    this._group.remove(this._pin1);
    this._group.remove(this._pin2);
    this._group.remove(this._line);
    this._group.remove(this._dottedLine);
    this._group.remove(this._arrow1);
    this._group.remove(this._arrow2);
    this._group.remove(this._textSprite);

    this._arrow1.dispose();
    this._arrow2.dispose();
    this._textSprite.dispose();

    this._pin1.geometry.dispose();
    this._pin1.geometry = undefined;
    this._pin1.material.dispose();
    this._pin1.material = undefined;
    this._pin1 = undefined;

    this._pin2.geometry.dispose();
    this._pin2.geometry = undefined;
    this._pin2.material.dispose();
    this._pin2.material = undefined;
    this._pin2 = undefined;

    // this._lineGeometry.dispose();
    // this._lineGeometry = undefined;
    this._lineMaterial.dispose();
    this._lineMaterial = undefined;
    this._line.dispose();
    this._line = undefined;

    // this._dottedLineGeometry.dispose();
    // this._dottedLineGeometry = undefined;
    this._dottedLineMaterial.dispose();
    this._dottedLineMaterial = undefined;
    this._dottedLine = undefined;

    // this._sphereGeometry.dispose();
    this._sphereMaterial.dispose();
    // this._sphereGeometry = undefined;
    this._sphereMaterial = undefined;
    this._arrow1 = undefined;
    this._arrow2 = undefined;
    this._textSprite = undefined;

    this._viewer.updateRenderer();
  }

  public render() {
    const sortedPoints = [...this._points].sort((a, b) => a.z - b.z);
    const pointOfIntersection = new Vector3(
      sortedPoints[1].x,
      sortedPoints[1].y,
      sortedPoints[0].z
    );

    this._line.updatePoints(sortedPoints[1], pointOfIntersection);
    this._line.visible = true;

    this._dottedLine.prepareRendering(sortedPoints[0], pointOfIntersection);

    this._dottedLine.visible = true;
    this._dottedLine.computeLineDistances();

    this._pin1.visible = false;
    this._pin2.visible = false;
    this._arrow1.visible = false;
    this._arrow2.visible = false;

    switch (this._mode) {
      case MeasurementMode.EDIT:
        this._lineMaterial.color.setHex(Config.EDIT_MEASUREMENT_COLOR);
        this._dottedLineMaterial.color.setHex(Config.EDIT_MEASUREMENT_COLOR);
        if (this._points[0]) {
          this._pin1.setPosition(this._points[0]);
          this._pin1.visible = true;
        }
        if (this._points[1]) {
          this._pin2.setPosition(this._points[1]);
          this._pin2.visible = true;
        }
        break;
      default:
        this._lineMaterial.color.setHex(Config.NORMAL_MEASUREMENT_COLOR);
        this._dottedLineMaterial.color.setHex(Config.NORMAL_MEASUREMENT_COLOR);
        if (sortedPoints[1]) {
          const direction = new Vector3()
            .subVectors(sortedPoints[1], pointOfIntersection)
            .normalize();
          const offset = direction.clone().multiplyScalar(1);
          this._arrow1.setPosition(sortedPoints[1]);
          this._arrow1.setOffset(offset);
          this._arrow1.lookAt(direction.add(sortedPoints[1]));
          this._arrow1.visible = true;
        }
        if (pointOfIntersection) {
          const direction = new Vector3()
            .subVectors(pointOfIntersection, sortedPoints[1])
            .normalize();
          const offset = direction.clone().multiplyScalar(1);
          this._arrow2.setPosition(pointOfIntersection);
          this._arrow2.setOffset(offset);
          this._arrow2.lookAt(direction.add(pointOfIntersection));
          this._arrow2.visible = true;
        }
    }

    if (this._points.length === 2) {
      this.updateTextContext2d(this.appearanceState);
      this._textSprite.setPosition(
        new Vector3()
          .addVectors(sortedPoints[1], pointOfIntersection)
          .multiplyScalar(0.5)
      );
      this._textSprite.visible = true;
    }
    this._viewer.updateRenderer();
  }

  public switchMode(mode: MeasurementMode) {
    this._mode = mode;
    this.render();
  }

  public onPositionUpdate(id: number, position: Vector3) {
    this._points[id].copy(position);
    this.calculateDistance();
    this.render();
  }

  public onUpdateComplete(id: number, position: Vector3) {
    this._viewer.setPointsToMeasurement(this._id, this._points);
  }

  public getPickableObjects(): Object3D[] {
    return [this._pin1, this._pin2];
  }

  public setVisibility(params: IMeasurementVisibleParams) {
    if (params.text !== undefined) {
      this._textSprite.visible = params.text;
    }
    if (params.measurement !== undefined) {
      this._group.visible = params.measurement;
    }
    this._viewer.updateRenderer();
  }

  public getVisibility(): boolean {
    return this._group.visible;
  }

  public changeAppearanceState(state: AutoAttenuableState): void {
    this.appearanceState = state;
    this._line.setMaterialByState(state);
    this._line.setGeometryByState(state);
    this._line.changeRenderOrderByState(state);

    this._arrow1.setMaterialByState(state);
    this._arrow1.setGeometryByState(state);
    this._arrow1.changeRenderOrderByState(state);

    this._arrow2.setMaterialByState(state);
    this._arrow2.setMaterialByState(state);
    this._arrow2.setMaterialByState(state);

    this._dottedLine.setSelectedMaterial(state === "selected");
    this._dottedLine.changeRenderOrderBySelected(state === "selected");

    this.updateTextContext2d(state);
    this._textSprite.changeRenderOrderByState(state);
  }

  public hitText(raycaster: Raycaster): Intersection[] {
    const objects = [this._textSprite, this._arrow1, this._arrow2, this._line];

    objects.forEach((object) => {
      object.geometry.computeBoundingBox();
      object.geometry.computeBoundingSphere();
    });
    return raycaster.intersectObjects(objects, true);
  }
}
